import { useNavigate } from 'react-router-dom';
import { HashLoader } from 'react-spinners';
import {
  CaretLeft,
  CaretDown,
  CheckCircle,
  Clock,
  XCircle,
  Money,
  Buildings,
  HandCoins,
  PlusCircle,
} from '@phosphor-icons/react';
import { useMyAdvanceRequests, type SalaryAdvanceRequest, type AdvanceStatus as AdvanceStatusType } from '../../hooks/useSalaryAdvance';
import { formatAsMoney } from '../../helpers/numbers';
import { useState } from 'react';

// ── Stage pipeline ──────────────────────────────────────────────────────────

type Stage = {
  key: AdvanceStatusType[];
  label: string;
  icon: React.ReactNode;
};

const STAGES: Stage[] = [
  {
    key: ['pending_hr', 'hr_approved', 'hr_declined', 'pending_disbursement', 'disbursed', 'declined'],
    label: 'Request Submitted',
    icon: <Money size={18} weight="fill" />,
  },
  {
    key: ['hr_approved', 'pending_disbursement', 'disbursed'],
    label: 'HR Approved',
    icon: <Buildings size={18} weight="fill" />,
  },
  {
    key: ['disbursed'],
    label: 'Funds Released',
    icon: <HandCoins size={18} weight="fill" />,
  },
];

type StageState = 'done' | 'active' | 'pending' | 'failed';

function activeStageIndex(status: AdvanceStatusType): number {
  switch (status) {
    case 'pending_hr': return 0;
    case 'hr_approved':
    case 'pending_disbursement': return 1;
    case 'hr_declined': return 1;
    case 'disbursed':
    case 'declined': return 2;
    default: return 0;
  }
}

// ── Status badge ────────────────────────────────────────────────────────────

const STATUS_META: Record<AdvanceStatusType, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  pending_hr: { label: 'Pending HR Review', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: <Clock size={14} weight="fill" /> },
  hr_approved: { label: 'HR Approved', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: <Clock size={14} weight="fill" /> },
  hr_declined: { label: 'HR Declined', color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: <XCircle size={14} weight="fill" /> },
  pending_disbursement: { label: 'Pending Disbursement', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: <Clock size={14} weight="fill" /> },
  disbursed: { label: 'Disbursed', color: 'text-green-700', bg: 'bg-green-50 border-green-200', icon: <CheckCircle size={14} weight="fill" /> },
  declined: { label: 'Declined', color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: <XCircle size={14} weight="fill" /> },
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-NG', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ── Sub-components ──────────────────────────────────────────────────────────

function StageTracker({ status }: { status: AdvanceStatusType }) {
  const activeIdx = activeStageIndex(status);
  const isDeclined = status === 'hr_declined' || status === 'declined';

  return (
    <div className="relative flex items-start gap-0">
      {STAGES.map((stage, idx) => {
        let state: StageState = 'pending';
        if (isDeclined && idx === activeIdx) state = 'failed';
        else if (idx < activeIdx) state = 'done';
        else if (idx === activeIdx) state = 'active';

        const isLast = idx === STAGES.length - 1;

        return (
          <div key={idx} className="flex-1 flex flex-col items-center gap-1">
            <div className="flex items-center w-full">
              {/* Dot */}
              <div
                className={`relative z-10 flex items-center justify-center rounded-full shrink-0 w-8 h-8 border-2 transition-colors ${
                  state === 'done'
                    ? 'bg-green-500 border-green-500 text-white'
                    : state === 'active'
                    ? 'bg-deepBlue border-deepBlue text-white'
                    : state === 'failed'
                    ? 'bg-red-500 border-red-500 text-white'
                    : 'bg-white border-gray-300 text-gray-400'
                }`}
              >
                {state === 'done' ? (
                  <CheckCircle size={16} weight="fill" />
                ) : state === 'failed' ? (
                  <XCircle size={16} weight="fill" />
                ) : (
                  stage.icon
                )}
              </div>
              {/* Connector */}
              {!isLast && (
                <div
                  className={`flex-1 h-0.5 ${idx < activeIdx ? 'bg-green-400' : 'bg-gray-200'}`}
                />
              )}
            </div>
            <span
              className={`text-[11px] text-center mt-1 leading-tight ${
                state === 'active'
                  ? 'text-deepBlue font-semibold'
                  : state === 'done'
                  ? 'text-green-600 font-medium'
                  : state === 'failed'
                  ? 'text-red-600 font-medium'
                  : 'text-gray-400'
              }`}
            >
              {stage.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function RepaymentTable({ schedule }: { schedule: SalaryAdvanceRequest['repayment_schedule'] }) {
  if (!schedule || schedule.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-2 font-medium text-gray-600">#</th>
            <th className="text-left py-3 px-2 font-medium text-gray-600">Due date</th>
            <th className="text-right py-3 px-2 font-medium text-gray-600">Amount</th>
            <th className="text-center py-3 px-2 font-medium text-gray-600">Status</th>
          </tr>
        </thead>
        <tbody>
          {schedule.map((row) => (
            <tr key={row.installment_number} className="border-b border-gray-100 hover:bg-gray-50/50">
              <td className="py-3 px-2 text-gray-700">{row.installment_number}</td>
              <td className="py-3 px-2 text-gray-700">{formatDate(row.due_date)}</td>
              <td className="py-3 px-2 text-right font-medium">₦{formatAsMoney(row.amount)}</td>
              <td className="py-3 px-2 text-center">
                {row.status === 'paid' ? (
                  <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                    <CheckCircle size={14} weight="fill" />
                    Paid
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                    <Clock size={14} weight="fill" />
                    Pending
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdvanceCard({ advance, defaultOpen }: { advance: SalaryAdvanceRequest; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const meta = STATUS_META[advance.status];

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Collapsed header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50/60 transition"
      >
        <div className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${meta.bg} ${meta.color}`}>
          {meta.icon}
          {meta.label}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900">₦{formatAsMoney(advance.amount)}</div>
          <div className="text-xs text-gray-500 mt-0.5">{formatDate(advance.created_at)} · {advance.repayment_months} months</div>
        </div>
        <CaretDown
          size={18}
          className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="border-t border-gray-100 p-5 flex flex-col gap-5">
          {/* Stage tracker */}
          <StageTracker status={advance.status} />

          {/* Status messages */}
          {advance.status === 'pending_hr' && (
            <div className="flex gap-3 p-4 rounded-xl bg-amber-50 border border-amber-100 text-sm text-amber-800">
              <Clock size={18} className="shrink-0 mt-0.5 text-amber-500" weight="fill" />
              <span>Your request is waiting for your organisation's HR admin to review it.</span>
            </div>
          )}
          {(advance.status === 'hr_approved' || advance.status === 'pending_disbursement') && (
            <div className="flex gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100 text-sm text-blue-800">
              <Clock size={18} className="shrink-0 mt-0.5 text-blue-500" weight="fill" />
              <span>HR approved. Your request has been forwarded to our disbursement partner for final review.</span>
            </div>
          )}
          {advance.status === 'hr_declined' && (
            <div className="flex gap-3 p-4 rounded-xl bg-red-50 border border-red-100 text-sm text-red-800">
              <XCircle size={18} className="shrink-0 mt-0.5 text-red-500" weight="fill" />
              <div>
                <p className="font-medium">Your organisation declined this request.</p>
                {advance.hr_note && <p className="mt-1 opacity-80">Note: {advance.hr_note}</p>}
              </div>
            </div>
          )}
          {advance.status === 'declined' && (
            <div className="flex gap-3 p-4 rounded-xl bg-red-50 border border-red-100 text-sm text-red-800">
              <XCircle size={18} className="shrink-0 mt-0.5 text-red-500" weight="fill" />
              <div>
                <p className="font-medium">The disbursement partner declined this request.</p>
                {advance.partner_note && <p className="mt-1 opacity-80">Note: {advance.partner_note}</p>}
              </div>
            </div>
          )}
          {advance.status === 'disbursed' && (
            <div className="flex gap-3 p-4 rounded-xl bg-green-50 border border-green-100 text-sm text-green-800">
              <CheckCircle size={18} className="shrink-0 mt-0.5 text-green-600" weight="fill" />
              <span>
                ₦{formatAsMoney(advance.amount)} has been credited to your Visaro wallet.
                {advance.disbursed_at && ` Disbursed on ${formatDate(advance.disbursed_at)}.`}
              </span>
            </div>
          )}

          {/* Summary grid */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: 'Amount', value: `₦${formatAsMoney(advance.amount)}` },
              { label: 'Period', value: `${advance.repayment_months} months` },
              { label: 'Total payable', value: advance.total_payable ? `₦${formatAsMoney(advance.total_payable)}` : '—' },
              { label: 'Per installment', value: advance.per_installment_amount ? `₦${formatAsMoney(advance.per_installment_amount)}` : '—' },
            ].map(({ label, value }) => (
              <div key={label} className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="font-semibold text-gray-900 mt-0.5">{value}</p>
              </div>
            ))}
          </div>

          {advance.reason && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Reason</p>
              <p className="text-sm text-gray-700">{advance.reason}</p>
            </div>
          )}

          {/* Repayment schedule */}
          {advance.status === 'disbursed' && advance.repayment_schedule && advance.repayment_schedule.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-3">Repayment schedule</p>
              <RepaymentTable schedule={advance.repayment_schedule} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

const AdvanceStatus = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMyAdvanceRequests();

  const advances: SalaryAdvanceRequest[] = (data as any)?.data ?? [];
  const hasActiveRequest = advances.some(
    (a) => a.status === 'pending_hr' || a.status === 'hr_approved' || a.status === 'pending_disbursement',
  );

  return (
    <div className="flex flex-col items-start gap-5 px-5 py-5 pb-20 max-w-3xl mx-auto w-full">
      <div className="flex items-center justify-between w-full">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 py-3 text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
        >
          <CaretLeft weight="bold" />
          Back
        </button>

        {!hasActiveRequest && (
          <button
            type="button"
            onClick={() => navigate('..', { relative: 'path' })}
            className="flex items-center gap-2 bg-deepBlue text-white px-5 py-3 rounded-xl font-medium text-sm"
          >
            <PlusCircle size={18} weight="fill" />
            New Request
          </button>
        )}
      </div>

      <div className="w-full">
        <h1 className="text-xl font-semibold text-gray-900">My Advance Requests</h1>
        <p className="text-sm text-gray-500 mt-1">Track the status of your salary advance requests.</p>
      </div>

      {isLoading && (
        <div className="flex justify-center w-full py-16">
          <HashLoader size={36} />
        </div>
      )}

      {isError && (
        <div className="w-full text-center py-12 text-gray-500">
          <p className="font-medium">Unable to load requests.</p>
          <p className="text-sm mt-1">Please check your connection and try again.</p>
        </div>
      )}

      {!isLoading && !isError && advances.length === 0 && (
        <div className="w-full flex flex-col items-center gap-4 py-16 text-center">
          <div className="p-4 bg-gray-100 rounded-full text-gray-400">
            <Money size={36} />
          </div>
          <div>
            <p className="font-semibold text-gray-700">No requests yet</p>
            <p className="text-sm text-gray-500 mt-1">Submit your first salary advance request to get started.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('..', { relative: 'path' })}
            className="flex items-center gap-2 bg-deepBlue text-white px-6 py-3 rounded-xl font-medium"
          >
            <PlusCircle size={18} weight="fill" />
            Request Advance
          </button>
        </div>
      )}

      {!isLoading && !isError && advances.length > 0 && (
        <div className="flex flex-col gap-3 w-full">
          {advances.map((adv, idx) => (
            <AdvanceCard key={adv.id} advance={adv} defaultOpen={idx === 0} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdvanceStatus;
